// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AccountForm } from '@client/banks/components/shared/account-form';
import { LabelWithInfoTooltip } from '@client/banks/components/shared/label-with-info-tooltip';
import type { AccountFormValues } from '@client/banks/hooks/use-account-form';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { type MutableRefObject } from 'react';
import { FormProvider } from 'react-hook-form';
import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';
import formStyles from '../../bank-upload/styles/bank-upload-form.module.css';
import {
  useLinkedLiquiditySection,
  type LinkedLiquidityGate,
  type LinkedLiquiditySectionApi,
} from './use-linked-liquidity-section';

export type LinkedLiquiditySectionProps = {
  /** Instrument RHF field name for linked iban (usually `iban`). */
  name?: 'iban';
  currencyOptions: readonly { value: string; label: string }[];
  accountIbanOptions: readonly { value: string; label: string }[];
  defaultCurrency: string;
  labelI18nKey?: string;
  apiRef?: MutableRefObject<LinkedLiquiditySectionApi | null>;
  onGateChange?: (gate: LinkedLiquidityGate) => void;
};

export const LinkedLiquiditySection = defineComponent({
  displayName: 'LinkedLiquiditySection',
  useSetup: useLinkedLiquiditySection,
  View: ({
    t,
    selectorValue,
    selectorOptions,
    showAccountForm,
    onSelectorChange,
    accountForm,
    currencyOptions,
    labelI18nKey,
  }) => (
    <div
      className={formStyles['form-section']}
      data-testid="bank-upload-linked-liquidity-section"
    >
      <div className={catalogStyles['field-stack']}>
        <LabelWithInfoTooltip tooltipI18nKey="aggregator.create-bank.linked-account-tooltip">
          <Translation
            i18nKey={
              (labelI18nKey ??
                'aggregator.create-bank.positions.account') as 'aggregator.create-bank.positions.account'
            }
            ns="aggregator"
          />
        </LabelWithInfoTooltip>
        <OptionsSelector
          optionsAvailable={[...selectorOptions]}
          optionSelected={selectorValue}
          onChangeValueSelected={onSelectorChange}
          defaultText={t('aggregator.create-bank.linked-account-none', {
            ns: 'aggregator',
          })}
          selectType="white"
          data-testid="bank-upload-linked-liquidity-selector"
        />
      </div>

      {showAccountForm ? (
        <div className="mt-4">
          <FormProvider {...accountForm}>
            <AccountForm
              formId="linked-liquidity-account-form"
              embed
              ibanRequired
              currencyOptions={currencyOptions}
              onSubmit={(_data: AccountFormValues) => undefined}
            />
          </FormProvider>
        </div>
      ) : null}
    </div>
  ),
});
